import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage } from "../../../customComponents/CustomAlertMessage";
import "./securityLogs.css";

const TAB_LOGINS = "logins";
const TAB_SETTINGS = "settings";

const LOGIN_COLUMNS = ["Type", "Time", "Status", "IP", "Login Location"];
const SETTINGS_COLUMNS = ["Type", "Time", "IP", "Actions"];

/** Normalizes API `data` into an array of log entries (`{ logs, total, ... }` or array). */
const normalizeLogsPayload = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object" && Array.isArray(raw.logs)) return raw.logs;
  if (typeof raw === "object" && Array.isArray(raw.items)) return raw.items;
  if (typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [raw];
};

const pad2 = (n) => String(n).padStart(2, "0");

/** `YYYY-MM-DD HH:mm:ss` from date / createdAt */
const formatAbsoluteTime = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

/** Matches UI: "45 min ago", "18 hours 22 min ago", "7 days 19 hours ago" */
const formatRelativeTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  let diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 0) diffSec = 0;
  if (diffSec < 60) return "just now";
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) {
    return remMins > 0 ? `${hours} hours ${remMins} min ago` : `${hours} hours ago`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days} days ${remHours} hours ago` : `${days} days ago`;
};

const formatTimeCell = (ts, { withRelative = false } = {}) => {
  const abs = formatAbsoluteTime(ts);
  if (!withRelative || abs === "—") return abs;
  const rel = formatRelativeTime(ts);
  if (!rel) return abs;
  return (
    <span className="slg-timeCell">
      <span className="slg-timeAbs">{abs}</span>
      <span className="slg-timeRel">{rel}</span>
    </span>
  );
};

/** Desktop → WEB, Mobile/Tablet → APP (matches Logins / Settings Type column). */
const formatType = (item) => {
  const platform = item?.metadata?.platform || item?.metadata?.client || item?.metadata?.source;
  if (platform && typeof platform === "string") {
    const p = platform.trim().toUpperCase();
    if (p === "WEB" || p === "APP" || p === "API") return p;
  }
  const device = String(item?.device?.device || "").toLowerCase();
  if (device.includes("mobile") || device.includes("tablet") || device.includes("phone")) {
    return "APP";
  }
  return "WEB";
};

const formatStatus = (s) => {
  if (s == null || s === "") return "—";
  const t = String(s).trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : "—";
};

const formatLoginLocation = (item) => {
  const loc = item?.location || {};
  if (loc.country) return loc.country;
  if (loc.city && loc.region) return `${loc.city}, ${loc.region}`;
  if (loc.city) return loc.city;
  if (loc.region) return loc.region;
  return "—";
};

const logTimestamp = (item) => item?.createdAt || item?.date || item?.updatedAt;

const mapLoginLogToRow = (item, idx) => {
  const ts = logTimestamp(item);
  return {
    id: item?._id || item?.id || idx,
    type: formatType(item),
    time: formatTimeCell(ts, { withRelative: true }),
    status: formatStatus(item?.status),
    ip: item?.IP || item?.ip || "—",
    location: formatLoginLocation(item),
  };
};

const mapSecuritySettingsLogToRow = (item, idx) => {
  const ts = logTimestamp(item);
  const actions = item?.Activity || item?.activity || "—";
  return {
    id: item?._id || item?.id || idx,
    type: formatType(item),
    time: formatTimeCell(ts, { withRelative: false }),
    ip: item?.IP || item?.ip || item?.metadata?.ip || "—",
    actions,
  };
};

/**
 * Security Logs — standalone page with tabs.
 * Route: /user_profile/security/securityLogs
 */
const SecurityLogs = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(TAB_LOGINS);

  const [loginRows, setLoginRows] = useState([]);
  const [settingsRows, setSettingsRows] = useState([]);

  const fetchLoginLogs = useCallback(async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securityGetLoginLogs();
      if (!result?.success) {
        alertErrorMessage(result?.message || "Failed to load login logs");
        setLoginRows([]);
        return;
      }
      const list = normalizeLogsPayload(result?.data);
      const sorted = [...list].sort((a, b) => {
        const ta = new Date(logTimestamp(a) || 0).getTime();
        const tb = new Date(logTimestamp(b) || 0).getTime();
        return tb - ta;
      });
      setLoginRows(sorted.map(mapLoginLogToRow));
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      setLoginRows([]);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  const fetchSecurityLogs = useCallback(async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securityGetSecurityLogs();
      if (!result?.success) {
        alertErrorMessage(result?.message || "Failed to load security settings logs");
        setSettingsRows([]);
        return;
      }
      const list = normalizeLogsPayload(result?.data);
      const sorted = [...list].sort((a, b) => {
        const ta = new Date(logTimestamp(a) || 0).getTime();
        const tb = new Date(logTimestamp(b) || 0).getTime();
        return tb - ta;
      });
      setSettingsRows(sorted.map(mapSecuritySettingsLogToRow));
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      setSettingsRows([]);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  useEffect(() => {
    if (tab === TAB_LOGINS) {
      fetchLoginLogs();
    }
  }, [tab, fetchLoginLogs]);

  useEffect(() => {
    if (tab === TAB_SETTINGS) {
      fetchSecurityLogs();
    }
  }, [tab, fetchSecurityLogs]);

  const { columns, rows } = useMemo(() => {
    if (tab === TAB_LOGINS) {
      return { columns: LOGIN_COLUMNS, rows: loginRows };
    }
    return { columns: SETTINGS_COLUMNS, rows: settingsRows };
  }, [tab, loginRows, settingsRows]);

  const colCount = columns.length;
  const isEmpty = rows.length === 0;

  return (
    <main className="slg-page" aria-labelledby="slg-title">
      <nav className="slg-page__crumbs" aria-label="Breadcrumb">
        <ol className="slg-page__crumbList">
          <li className="slg-page__crumbItem">
            <button type="button" className="slg-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="slg-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="slg-page__crumbItem slg-page__crumbItem--active" aria-current="page">
            Security Logs
          </li>
        </ol>
      </nav>

      <div className="security_section_bl">
        <h1 id="slg-title" className="slg-page__title">
          Security Logs
        </h1>

        <div className="slg-tabs" role="tablist" aria-label="Log type">
          <button
            type="button"
            role="tab"
            aria-selected={tab === TAB_LOGINS}
            id="slg-tab-logins"
            aria-controls="slg-panel"
            className={`slg-tab ${tab === TAB_LOGINS ? "is-active" : ""}`}
            onClick={() => setTab(TAB_LOGINS)}
          >
            Logins History
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === TAB_SETTINGS}
            id="slg-tab-settings"
            aria-controls="slg-panel"
            className={`slg-tab ${tab === TAB_SETTINGS ? "is-active" : ""}`}
            onClick={() => setTab(TAB_SETTINGS)}
          >
            Security Settings History
          </button>
        </div>

        <div
          id="slg-panel"
          role="tabpanel"
          aria-labelledby={tab === TAB_LOGINS ? "slg-tab-logins" : "slg-tab-settings"}
          className="slg-tableWrap slg-tableWrap--mobileCards"
        >
          <div className="slg-tableScroll">
            <table className="slg-table">
              <thead>
                <tr>
                  {columns.map((label) => (
                    <th key={label} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isEmpty ? (
                  <tr className="slg-table__emptyRow">
                    <td colSpan={colCount}>
                      <div className="slg-empty" aria-live="polite">
                        <img
                          src="/images/no-data.svg"
                          alt=""
                          width={120}
                          height={144}
                          className="spot_orders_empty_telescope"
                        />
                        <p className="slg-empty__text">No data</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const cells =
                      tab === TAB_LOGINS
                        ? [row.type, row.time, row.status, row.ip, row.location]
                        : [row.type, row.time, row.ip, row.actions];
                    return (
                      <tr key={row.id ?? idx}>
                        {cells.map((cell, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <td key={i} data-label={columns[i]}>
                            <span className="slg-cardVal">{cell}</span>
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SecurityLogs;
